import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { apiFetch, setApiToken } from '../lib/api'

export type Role = 'admin' | 'rh'
export type UserType = 'usuario' | 'cliente'

export type CompanyMembership = {
  companyId: string
  companyName: string
  role: Role
}

export type Me = {
  id: string
  email: string
  userType: UserType
  mustAcceptTerms: boolean
  acceptedTermsVersion: string | null
  memberships: CompanyMembership[]
}

type AuthContextValue = {
  token: string | null
  me: Me | null
  loading: boolean
  selectedCompanyId: string | null
  setSelectedCompanyId: (companyId: string | null) => void
  login: (email: string, password: string) => Promise<Me>
  logout: () => void
  refreshMe: () => Promise<void>
  acceptTerms: (version: string) => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

function getStorage(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function setStorage(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch {
    // ignore
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Token NÃO persistido: sempre exige login ao recarregar.
  const [token, setToken] = useState<string | null>(null)
  const [me, setMe] = useState<Me | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedCompanyId, setSelectedCompanyIdState] = useState<string | null>(() =>
    getStorage('nccrh_companyId')
  )

  const setSelectedCompanyId = (companyId: string | null) => {
    setSelectedCompanyIdState(companyId)
    setStorage('nccrh_companyId', companyId)
  }

  const refreshMe = async () => {
    if (!token) {
      setMe(null)
      return
    }
    try {
      const data = await apiFetch<{ me: Me }>('/me', { auth: true, quiet: true })
      if (!data || typeof data !== 'object' || !(data as any).me) throw new Error('Resposta inválida')
      setMe(data.me)
      const membershipIds = (data.me?.memberships ?? []).map((m) => m.companyId)
      if (membershipIds.length === 0) {
        setSelectedCompanyId(null)
      } else if (!selectedCompanyId || !membershipIds.includes(selectedCompanyId)) {
        setSelectedCompanyId(data.me!.memberships![0]!.companyId)
      }
    } catch {
      // token expirado/inválido → força logout sem quebrar a UI
      setApiToken(null)
      setToken(null)
      setMe(null)
    }
  }

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        if (token) await refreshMe()
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const login = async (email: string, password: string) => {
    const data = await apiFetch<{ token: string; me: Me }>('/auth/login', {
      auth: false,
      method: 'POST',
      body: JSON.stringify({ email, password })
    })
    setToken(data.token)
    setApiToken(data.token)
    setMe(data.me)
    if (data.me.memberships.length) setSelectedCompanyId(data.me.memberships[0]!.companyId)
    return data.me
  }

  const logout = () => {
    setToken(null)
    setMe(null)
    setSelectedCompanyId(null)
    setApiToken(null)
  }

  const acceptTerms = async (version: string) => {
    await apiFetch('/auth/accept-terms', { method: 'POST', body: JSON.stringify({ version }) })
    await refreshMe()
  }

  const value = useMemo<AuthContextValue>(
    () => ({
      token,
      me,
      loading,
      selectedCompanyId,
      setSelectedCompanyId,
      login,
      logout,
      refreshMe,
      acceptTerms
    }),
    [token, me, loading, selectedCompanyId]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth deve ser usado dentro de AuthProvider')
  return ctx
}

